import type { ReactElement } from "react";

/**
 * Every sticker's drawing, keyed by sticker id. Each one is drawn on a
 * 120×120 grid in flat fills under one ink outline; `StickerBlob` paints it
 * three times — a fat white copy for the die-cut edge, the drawing itself,
 * and a silhouette mask for the laminate — so nothing here sets its own
 * border, shadow or gloss.
 *
 * Outline defaults (ink, 3 wide, round) come from the wrapping group:
 * `stroke="none"` for a borderless fill, `strokeWidth` for a thinner or
 * heavier line. Never `style` — the die-cut pass restyles every element with
 * CSS, which only beats presentation attributes.
 */

export const STICKER_INK = "#1f1640";
const HONEY = "#f6b938";
const HONEY_DARK = "#d98a12";
const CREAM = "#fff4d6";
const CORAL = "#ff7a59";
const SKY = "#7cc4ff";
const MINT = "#5fd3a6";
const LILAC = "#c9b6ff";
const PURPLE = "#6a33ff";
const PINK = "#ff9ec7";
const LIME = "#c8e65a";
const PAPER = "#f5f2fb";
const WHITE = "#ffffff";
const DASH = "3 6";

function Bee({
  transform,
  fill = HONEY,
}: {
  transform: string;
  fill?: string;
}) {
  return (
    <g transform={transform}>
      <ellipse
        cx={-4}
        cy={-10}
        rx={7}
        ry={5}
        fill={WHITE}
        transform="rotate(-25 -4 -10)"
      />
      <ellipse
        cx={5}
        cy={-10}
        rx={7}
        ry={5}
        fill={WHITE}
        transform="rotate(25 5 -10)"
      />
      <ellipse cx={0} cy={0} rx={12} ry={9} fill={fill} />
      <path d="M-3 -8.6 V8.6 M4 -8.2 V8.2" fill="none" />
      <circle cx={8.5} cy={-2} r={1.8} fill={STICKER_INK} stroke="none" />
    </g>
  );
}

function Label({
  x,
  y,
  size,
  fill,
  children,
}: {
  x: number;
  y: number;
  size: number;
  fill: string;
  children: string;
}) {
  return (
    <text
      x={x}
      y={y}
      fontSize={size}
      fontWeight={800}
      textAnchor="middle"
      fill={fill}
      stroke="none"
    >
      {children}
    </text>
  );
}

const SMALL_HEART =
  "M0 8 C-10 2, -10 -8, -4 -8 C-1 -8, 0 -5, 0 -5 C0 -5, 1 -8, 4 -8 C10 -8, 10 2, 0 8 Z";
const STAR =
  "M60 16 L71 46 L102 47 L78 67 L86 98 L60 81 L34 98 L42 67 L18 47 L49 46 Z";
const FLAME =
  "M60 10 C72 30, 94 44, 92 72 C90 96, 74 108, 60 108 C44 108, 28 96, 28 74 C28 56, 40 48, 44 32 C50 42, 54 46, 56 50 C58 36, 56 22, 60 10 Z";
const BAG = "M26 42 H94 L88 106 H32 Z";

const MOUNTAIN = (
  <>
    <polygon points="10,102 46,36 64,62 78,44 110,102" fill={SKY} />
    <path d="M38 51 L46 36 L54 51 L50 48 L46 53 L42 48 Z" fill={WHITE} />
    <path
      d="M22 100 C40 88, 70 94, 60 76 S74 60, 78 46"
      fill="none"
      strokeWidth={2}
      strokeDasharray={DASH}
    />
    <path d="M78 44 V24" fill="none" />
    <polygon points="78,24 94,29 78,34" fill={CORAL} />
  </>
);

export const STICKER_ART: Record<string, ReactElement> = {
  "event-1": (
    <>
      <path
        d="M18 34 C34 14, 52 44, 70 28"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <g transform="rotate(-10 60 64)">
        <rect x={18} y={46} width={84} height={44} rx={9} fill={HONEY} />
        <path
          d="M76 50 V86"
          fill="none"
          strokeWidth={2}
          strokeDasharray={DASH}
        />
        <path d="M32 62 h28 M32 74 h18" fill="none" />
      </g>
      <Bee transform="translate(88 26)" />
    </>
  ),
  "event-5": (
    <>
      {[
        [-36, SKY],
        [-18, CORAL],
        [36, MINT],
        [18, LILAC],
        [0, HONEY],
      ].map(([angle, fill]) => (
        <rect
          key={angle}
          x={46}
          y={22}
          width={28}
          height={62}
          rx={6}
          fill={fill as string}
          transform={`rotate(${angle} 60 104)`}
        />
      ))}
      <path d="M50 66 H70" fill="none" strokeWidth={2} strokeDasharray={DASH} />
      <path
        d="M60 32 l3 6 6 1 -4.5 4.3 1 6.4 -5.5 -3 -5.5 3 1 -6.4 -4.5 -4.3 6 -1 z"
        fill={STICKER_INK}
        stroke="none"
      />
    </>
  ),
  "event-10": (
    <>
      <path d={FLAME} fill={CORAL} />
      <path
        d="M60 50 C68 62, 78 70, 76 86 C74 98, 66 102, 60 102 C52 102, 44 96, 44 86 C44 74, 54 66, 60 50 Z"
        fill={HONEY}
      />
      <Label x={60} y={95} size={20} fill={STICKER_INK}>
        10
      </Label>
    </>
  ),
  "event-25": (
    <>
      <polygon points="30,28 44,20 108,96 54,106" fill={CREAM} stroke="none" />
      <ellipse cx={80} cy={100} rx={28} ry={8} fill={HONEY} />
      <rect
        x={12}
        y={16}
        width={30}
        height={18}
        rx={5}
        fill={STICKER_INK}
        transform="rotate(-35 26 24)"
      />
      <Bee transform="translate(80 80) scale(1.4)" />
      <path d="M98 40 v10 M93 45 h10" fill="none" strokeWidth={2} />
    </>
  ),
  "hiive-1": (
    <>
      <polygon
        points="60,24 94.6,44 94.6,84 60,104 25.4,84 25.4,44"
        fill={HONEY}
      />
      <polygon
        points="60,48 73.9,56 73.9,72 60,80 46.1,72 46.1,56"
        fill={HONEY_DARK}
      />
      <path
        d="M110 6 C104 2, 96 10, 96 18"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <Bee transform="translate(88 26) scale(-1 1) rotate(-30)" />
    </>
  ),
  "hiive-3": (
    <>
      <polygon
        points="60,16 79.05,27 79.05,49 60,60 40.95,49 40.95,27"
        fill={PURPLE}
      />
      <polygon
        points="41,49 60.05,60 60.05,82 41,93 21.95,82 21.95,60"
        fill={HONEY}
      />
      <polygon
        points="79,49 98.05,60 98.05,82 79,93 59.95,82 59.95,60"
        fill={LILAC}
      />
      <Bee transform="translate(60 64) scale(0.75)" />
    </>
  ),
  "post-10": (
    <>
      <path
        d="M36 20 H84 A18 18 0 0 1 102 38 V64 A18 18 0 0 1 84 82 H54 L34 100 L38 82 H36 A18 18 0 0 1 18 64 V38 A18 18 0 0 1 36 20 Z"
        fill={SKY}
      />
      <path d="M66 28 L46 56 H60 L52 76 L76 46 H62 Z" fill={HONEY} />
    </>
  ),
  "post-50": (
    <>
      <path d="M42 72 L48 96 H60 L56 74 Z" fill={CORAL} />
      <rect x={14} y={48} width={18} height={26} rx={5} fill={CREAM} />
      <polygon points="30,48 80,24 80,98 30,74" fill={CORAL} />
      <path d="M90 44 Q100 61 90 78 M100 32 Q118 61 100 90" fill="none" />
    </>
  ),
  "review-1": (
    <>
      <path d={STAR} fill={HONEY} />
      <circle cx={52} cy={60} r={3} fill={STICKER_INK} stroke="none" />
      <circle cx={68} cy={60} r={3} fill={STICKER_INK} stroke="none" />
      <path d="M53 69 Q60 75 67 69" fill="none" />
      <path
        d="M102 18 v10 M97 23 h10 M18 88 v8 M14 92 h8"
        fill="none"
        strokeWidth={2}
      />
    </>
  ),
  "review-5": (
    <>
      <polygon points="44,70 34,110 47,101 56,112 60,76" fill={LILAC} />
      <polygon points="76,70 86,110 73,101 64,112 60,76" fill={LILAC} />
      <circle cx={60} cy={50} r={34} fill={PURPLE} />
      <circle
        cx={60}
        cy={50}
        r={26}
        fill="none"
        stroke={WHITE}
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <path
        d="M60 31 L65 44 L79 44 L68 53 L72 67 L60 59 L48 67 L52 53 L41 44 L55 44 Z"
        fill={WHITE}
        stroke={WHITE}
        strokeWidth={2}
      />
    </>
  ),
  "shop-1": (
    <>
      <path
        d="M46 44 V34 A14 14 0 0 1 74 34 V44"
        fill="none"
        strokeWidth={4.5}
      />
      <path d={BAG} fill={MINT} />
      <path
        d="M60 92 C42 81, 44 64, 53 64 C57 64, 60 69, 60 69 C60 69, 63 64, 67 64 C76 64, 78 81, 60 92 Z"
        fill={PINK}
      />
    </>
  ),
  "shop-5": (
    <>
      <path
        d="M24 12 H76 V96 L70 102 L64 96 L58 102 L52 96 L46 102 L40 96 L34 102 L28 96 L24 100 Z"
        fill={CREAM}
      />
      <path d="M34 28 H66 M34 40 H60 M34 52 H66 M34 64 H54" fill="none" />
      <path d="M34 80 H66" fill="none" strokeWidth={4.5} />
      <path d="M78 72 V66 A9 9 0 0 1 96 66 V72" fill="none" />
      <path d="M66 70 H108 L104 108 H70 Z" fill={MINT} />
    </>
  ),
  "venue-1": (
    <>
      <ellipse cx={60} cy={106} rx={20} ry={5} fill={LILAC} />
      <path
        d="M60 104 C60 104 26 70 26 46 A34 34 0 0 1 94 46 C94 70 60 104 60 104 Z"
        fill={CORAL}
      />
      <circle cx={60} cy={46} r={17} fill={CREAM} />
      <path d="M52 46 L58 52 L69 40" fill="none" strokeWidth={4.5} />
    </>
  ),
  "venue-5": (
    <>
      <polygon points="14,28 44,20 44,94 14,102" fill={SKY} />
      <polygon points="44,20 76,28 76,102 44,94" fill={CREAM} />
      <polygon points="76,28 106,20 106,94 76,102" fill={SKY} />
      <path
        d="M26 84 C38 60, 56 80, 62 56 S86 44, 94 36"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <circle cx={26} cy={84} r={5} fill={CORAL} />
      <circle cx={62} cy={58} r={5} fill={CORAL} />
      <circle cx={94} cy={36} r={5} fill={CORAL} />
    </>
  ),
  "comment-5": (
    <>
      <path d="M30 66 L24 84 L46 68" fill={LILAC} />
      <rect x={14} y={22} width={68} height={48} rx={17} fill={LILAC} />
      <path d="M90 90 L98 104 L78 92" fill={HONEY} />
      <rect x={42} y={52} width={64} height={42} rx={16} fill={HONEY} />
      {[60, 74, 88].map((cx) => (
        <circle
          key={cx}
          cx={cx}
          cy={73}
          r={3.6}
          fill={STICKER_INK}
          stroke="none"
        />
      ))}
    </>
  ),
  "comment-20": (
    <>
      <ellipse
        cx={48}
        cy={34}
        rx={12}
        ry={8}
        fill={WHITE}
        transform="rotate(-25 48 34)"
      />
      <ellipse
        cx={70}
        cy={32}
        rx={12}
        ry={8}
        fill={WHITE}
        transform="rotate(25 70 32)"
      />
      <path d="M34 80 L20 100 L48 88" fill={HONEY} />
      <ellipse cx={60} cy={64} rx={38} ry={28} fill={HONEY} />
      <path
        d="M46 38 Q40 64 46 90 M66 36 Q60 64 66 92"
        fill="none"
        strokeWidth={4.5}
      />
      <path d="M104 50 h8 M106 62 h10 M104 74 h8" fill="none" />
    </>
  ),
  "buddy-1": (
    <>
      <path
        d="M60 52 C44 42, 46 26, 54 26 C58 26, 60 31, 60 31 C60 31, 62 26, 66 26 C74 26, 76 42, 60 52 Z"
        fill={PINK}
      />
      <Bee transform="translate(34 78) scale(1.5)" />
      <Bee transform="translate(86 78) scale(-1.5 1.5)" fill={LILAC} />
    </>
  ),
  "buddy-5": (
    <>
      <Bee transform="translate(60 34)" />
      <Bee transform="translate(38 58) scale(0.85)" fill={LILAC} />
      <Bee transform="translate(82 58) scale(0.85)" fill={PINK} />
      <Bee transform="translate(20 84) scale(0.7)" fill={MINT} />
      <Bee transform="translate(100 84) scale(0.7)" fill={SKY} />
    </>
  ),
  "session-created-1": (
    <>
      <path d="M32 52 Q14 30 40 16 Q56 10 62 44" fill="none" strokeWidth={2} />
      <rect x={54} y={44} width={48} height={22} rx={7} fill={HONEY} />
      <circle cx={50} cy={72} r={24} fill={HONEY} />
      <circle cx={50} cy={72} r={7} fill={STICKER_INK} stroke="none" />
      <path d="M98 32 l8 -8 M104 46 h10 M88 24 l2 -10" fill="none" />
    </>
  ),
  "session-created-5": (
    <>
      <rect x={18} y={24} width={84} height={80} rx={13} fill={CREAM} />
      <path
        d="M31 24 H89 A13 13 0 0 1 102 37 V48 H18 V37 A13 13 0 0 1 31 24 Z"
        fill={CORAL}
      />
      <path d="M40 16 V32 M80 16 V32" fill="none" strokeWidth={4.5} />
      {[
        [34, 64],
        [50, 64],
        [66, 64],
        [34, 84],
        [50, 84],
      ].map(([cx, cy]) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={3.4}
          fill={STICKER_INK}
          stroke="none"
        />
      ))}
      <circle cx={80} cy={80} r={14} fill={LIME} />
      <path
        d="M68 74 Q80 82 92 74 M68 86 Q80 78 92 86"
        fill="none"
        strokeWidth={2}
      />
    </>
  ),
  "session-joined-1": (
    <>
      <path d="M12 46 H30 M6 60 H30 M12 74 H30" fill="none" />
      <circle cx={68} cy={60} r={30} fill={LIME} />
      <path
        d="M44 42 Q62 60 44 78 M92 42 Q74 60 92 78"
        fill="none"
        stroke={WHITE}
        strokeWidth={4.5}
      />
    </>
  ),
  "session-joined-10": (
    <g transform="rotate(-6 60 60)">
      <rect x={14} y={26} width={92} height={68} rx={11} fill={LILAC} />
      <path d="M26 40 H62" fill="none" strokeWidth={2} />
      {[58, 78].flatMap((cy) =>
        [30, 45, 60, 75, 90].map((cx) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r={6}
            fill={cx === 90 && cy === 78 ? HONEY : PAPER}
          />
        )),
      )}
    </g>
  ),
  "wins-1": (
    <>
      <path
        d="M24 84 L18 38 L42 60 L60 26 L78 60 L102 38 L96 84 Z"
        fill={HONEY}
      />
      <rect x={22} y={84} width={76} height={16} rx={5} fill={HONEY_DARK} />
      <circle cx={60} cy={70} r={7} fill={CORAL} />
      <circle cx={38} cy={72} r={4.5} fill={SKY} />
      <circle cx={82} cy={72} r={4.5} fill={SKY} />
      <circle cx={18} cy={38} r={4.5} fill={HONEY} />
      <circle cx={60} cy={26} r={4.5} fill={HONEY} />
      <circle cx={102} cy={38} r={4.5} fill={HONEY} />
    </>
  ),
  "wins-5": (
    <>
      <path
        d="M36 28 H24 A12 12 0 0 0 38 56 M84 28 H96 A12 12 0 0 1 82 56"
        fill="none"
        strokeWidth={4.5}
      />
      <rect x={53} y={70} width={14} height={16} fill={HONEY_DARK} />
      <rect x={38} y={84} width={44} height={16} rx={5} fill={HONEY_DARK} />
      <path d="M34 18 H86 V44 A26 26 0 0 1 34 44 Z" fill={HONEY} />
      <path d="M48 18 V30 A4 4 0 0 0 56 30 V18" fill={HONEY_DARK} />
      <path
        d="M60 32 L63.5 40 L72 40.5 L65.5 46 L67.5 54 L60 49.5 L52.5 54 L54.5 46 L48 40.5 L56.5 40 Z"
        fill={WHITE}
        strokeWidth={2}
      />
    </>
  ),
  "games-played-5": (
    <>
      {[
        [-34, CORAL],
        [34, SKY],
      ].map(([angle, fill]) => (
        <g key={angle} transform={`rotate(${angle} 60 80)`}>
          <rect x={55} y={66} width={10} height={34} rx={4} fill={CREAM} />
          <ellipse cx={60} cy={44} rx={20} ry={24} fill={fill as string} />
        </g>
      ))}
      <circle cx={60} cy={18} r={8} fill={LIME} />
    </>
  ),
  "games-played-25": (
    <>
      <path
        d="M60 14 L98 26 V56 C98 82 80 98 60 108 C40 98 22 82 22 56 V26 Z"
        fill={PURPLE}
      />
      <path d="M90 30 L80 40 L92 46" fill="none" strokeWidth={2} />
      <path
        d="M40 44 V78 M50 44 V78 M60 44 V78 M70 44 V78 M34 72 L78 50"
        fill="none"
        stroke={WHITE}
        strokeWidth={4.5}
      />
    </>
  ),
  "rating-1200": (
    <>
      <path
        d="M60 8 l4 8 9 1 -6.5 6 1.5 9 -8 -4.5 -8 4.5 1.5 -9 -6.5 -6 9 -1 z"
        fill={HONEY}
      />
      <path d="M26 50 L60 34 L94 50 V64 L60 48 L26 64 Z" fill={HONEY} />
      <path d="M26 72 L60 56 L94 72 V86 L60 70 L26 86 Z" fill={HONEY_DARK} />
      <rect x={32} y={90} width={56} height={24} rx={12} fill={STICKER_INK} />
      <Label x={60} y={108} size={16} fill={WHITE}>
        1200
      </Label>
    </>
  ),
  "noncomp-5": (
    <>
      <rect x={18} y={76} width={40} height={28} rx={5} fill={SKY} />
      <rect x={62} y={76} width={40} height={28} rx={5} fill={CORAL} />
      <rect x={38} y={46} width={44} height={28} rx={5} fill={HONEY} />
      <rect x={47} y={18} width={26} height={26} rx={5} fill={MINT} />
      <path d="M60 18 V4" fill="none" />
      <polygon points="60,4 74,8 60,12" fill={PURPLE} />
    </>
  ),
  "chat-50": (
    <>
      <path d="M74 38 L68 52 L84 42" fill={WHITE} />
      <rect x={62} y={10} width={48} height={32} rx={13} fill={WHITE} />
      <Label x={86} y={32} size={15} fill={STICKER_INK}>
        hi!
      </Label>
      <Bee transform="translate(46 76) scale(2.2)" />
    </>
  ),
  "chat-200": (
    <>
      <path
        d="M32 52 A28 28 0 0 0 88 52 M60 80 V100 M42 104 H78"
        fill="none"
        strokeWidth={4.5}
      />
      <rect x={42} y={12} width={36} height={56} rx={18} fill={LILAC} />
      <path d="M48 30 H72 M48 40 H72 M48 50 H72" fill="none" strokeWidth={2} />
      <path
        d="M100 20 v10 M95 25 h10 M16 30 v8 M12 34 h8"
        fill="none"
        strokeWidth={2}
      />
    </>
  ),
  "story-3": (
    <>
      <circle cx={60} cy={60} r={50} fill={WHITE} stroke="none" />
      <path
        d="M64 14.2 A46 46 0 0 1 101.7 79.4 M97.7 86.4 A46 46 0 0 1 22.3 86.4 M18.3 79.4 A46 46 0 0 1 56 14.2"
        fill="none"
        stroke={PURPLE}
        strokeWidth={6}
      />
      <rect x={46} y={36} width={20} height={12} rx={3} fill={PINK} />
      <rect x={32} y={44} width={56} height={40} rx={10} fill={PINK} />
      <circle cx={60} cy={64} r={12} fill={WHITE} />
      <circle cx={60} cy={64} r={6} fill={STICKER_INK} stroke="none" />
    </>
  ),
  "story-15": (
    <>
      <rect x={20} y={52} width={80} height={50} rx={7} fill={STICKER_INK} />
      <g transform="rotate(-14 20 50)">
        <rect x={20} y={34} width={80} height={15} rx={3} fill={WHITE} />
        <path
          d="M36 34 L30 49 M54 34 L48 49 M72 34 L66 49 M90 34 L84 49"
          fill="none"
          strokeWidth={4.5}
        />
      </g>
      <rect x={20} y={52} width={80} height={13} fill={WHITE} />
      <path
        d="M36 52 L30 65 M54 52 L48 65 M72 52 L66 65 M90 52 L84 65"
        fill="none"
        strokeWidth={4.5}
      />
      <Label x={60} y={92} size={17} fill={WHITE}>
        TAKE 15
      </Label>
    </>
  ),
  "workout-1": (
    <>
      <path d="M40 54 V40 A20 20 0 0 1 80 40 V54" fill="none" strokeWidth={9} />
      <path
        d="M40 54 V40 A20 20 0 0 1 80 40 V54"
        fill="none"
        stroke={WHITE}
        strokeWidth={3}
      />
      <path
        d="M32 104 H88 A6 6 0 0 0 92 94 A32 32 0 1 0 28 94 A6 6 0 0 0 32 104 Z"
        fill={CORAL}
      />
      <path d="M42 70 A20 20 0 0 1 54 58" fill="none" stroke={WHITE} />
    </>
  ),
  "workout-10": (
    <>
      <path d="M10 70 Q60 52 110 70" fill="none" strokeWidth={4.5} />
      <g transform="rotate(-10 26 66)">
        <rect x={16} y={40} width={13} height={52} rx={3} fill={PURPLE} />
        <rect x={29} y={48} width={9} height={36} rx={3} fill={LILAC} />
      </g>
      <g transform="rotate(10 94 66)">
        <rect x={91} y={40} width={13} height={52} rx={3} fill={PURPLE} />
        <rect x={82} y={48} width={9} height={36} rx={3} fill={LILAC} />
      </g>
      <path d="M50 34 l-4 -9 M60 32 V21 M70 34 l4 -9" fill="none" />
    </>
  ),
  "movement-1": (
    <>
      <path
        d="M20 86 C20 66 34 60 46 58 L58 44 C66 50 76 58 90 64 C100 68 106 74 106 86 Z"
        fill={MINT}
      />
      <path
        d="M14 86 H106 A6 6 0 0 1 106 98 H20 A6 6 0 0 1 14 92 Z"
        fill={WHITE}
      />
      <path
        d="M50 56 l8 8 M58 49 l8 8 M66 74 Q78 68 90 74"
        fill="none"
        strokeWidth={2}
      />
      <circle cx={8} cy={70} r={3} fill={LILAC} stroke="none" />
      <circle cx={4} cy={82} r={2.4} fill={LILAC} stroke="none" />
    </>
  ),
  "movement-10": MOUNTAIN,

  // ── Higher rungs ──────────────────────────────────────────────────────────
  "event-50": (
    <>
      <path
        d="M14 60 Q12 86 34 102 M106 60 Q108 86 86 102"
        fill="none"
        strokeWidth={2}
      />
      <path
        d="M14 66 q-6 -4 -2 -10 q6 2 2 10 M16 80 q-7 -2 -5 -9 q7 0 5 9 M22 92 q-7 0 -6 -8 q7 -1 6 8 M106 66 q6 -4 2 -10 q-6 2 -2 10 M104 80 q7 -2 5 -9 q-7 0 -5 9 M98 92 q7 0 6 -8 q-7 -1 -6 8"
        fill={MINT}
        stroke="none"
      />
      <rect x={24} y={12} width={72} height={86} rx={10} fill={HONEY} />
      <rect x={33} y={21} width={54} height={56} rx={5} fill={CREAM} />
      <Bee transform="translate(60 54) scale(1.5)" />
      <rect
        x={42}
        y={83}
        width={36}
        height={8}
        rx={2}
        fill={HONEY_DARK}
        strokeWidth={2}
      />
    </>
  ),
  "hiive-5": (
    <>
      <polygon
        points="45.3,23 60.02,31.5 60.02,48.5 45.3,57 30.58,48.5 30.58,31.5"
        fill={PURPLE}
      />
      <polygon
        points="74.7,23 89.42,31.5 89.42,48.5 74.7,57 59.98,48.5 59.98,31.5"
        fill={HONEY}
      />
      <polygon
        points="30.6,48.5 45.32,57 45.32,74 30.6,82.5 15.88,74 15.88,57"
        fill={LILAC}
      />
      <polygon
        points="60,48.5 74.72,57 74.72,74 60,82.5 45.28,74 45.28,57"
        fill={HONEY}
      />
      <polygon
        points="89.4,48.5 104.12,57 104.12,74 89.4,82.5 74.68,74 74.68,57"
        fill={PURPLE}
      />
      <path
        d="M20 98 Q35 112 50 98 Q65 112 80 98"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <Bee transform="translate(94 100) scale(0.8)" />
    </>
  ),
  "buddy-15": (
    <>
      <path
        d="M60 62 C76 62, 80 44, 66 38 C46 30, 30 52, 38 72 C46 92, 84 92, 92 66"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <Bee transform="translate(60 62)" />
      <Bee transform="translate(60 22) scale(0.6)" fill={LILAC} />
      <Bee transform="translate(94 38) scale(0.6)" fill={PINK} />
      <Bee transform="translate(102 78) scale(-0.6 0.6)" fill={MINT} />
      <Bee transform="translate(70 104) scale(-0.6 0.6)" fill={SKY} />
      <Bee transform="translate(32 102) scale(-0.6 0.6)" />
      <Bee transform="translate(16 66) scale(0.6)" fill={CORAL} />
      <Bee transform="translate(26 32) scale(0.6)" fill={PURPLE} />
    </>
  ),
  "wins-25": (
    <>
      <path
        d="M38 46 H26 A11 11 0 0 0 40 70 M82 46 H94 A11 11 0 0 1 80 70"
        fill="none"
        strokeWidth={4.5}
      />
      <rect x={54} y={80} width={12} height={14} fill="#4a2bb3" />
      <rect x={40} y={92} width={40} height={16} rx={5} fill="#4a2bb3" />
      <path d="M38 38 H82 V60 A22 22 0 0 1 38 60 Z" fill={PURPLE} />
      <path d="M52 58 h16" fill="none" stroke={WHITE} strokeWidth={2} />
      <path
        d="M40 38 L35 12 L50 24 L60 6 L70 24 L85 12 L80 38 Z"
        fill={HONEY}
      />
      <circle cx={60} cy={27} r={4} fill={CORAL} />
    </>
  ),
  "movement-50": (
    <>
      <rect x={12} y={14} width={96} height={92} rx={22} fill={SKY} />
      <circle cx={60} cy={50} r={18} fill={HONEY} />
      <path
        d="M12 50 H108 V84 A22 22 0 0 1 86 106 H34 A22 22 0 0 1 12 84 Z"
        fill={MINT}
      />
      <polygon points="55,50 65,50 94,106 26,106" fill="#3b3060" />
      <path
        d="M60 56 V61 M60 69 V78 M60 87 V100"
        fill="none"
        stroke={WHITE}
        strokeWidth={2}
      />
    </>
  ),
  "workout-50": (
    <>
      <path d="M82 21.9 A44 44 0 1 1 38 21.9" fill="none" strokeWidth={4.5} />
      <polygon
        points="47.5,16.4 41.5,28 34.5,15.8"
        fill={STICKER_INK}
        stroke="none"
      />
      <rect x={36} y={56} width={48} height={8} rx={2} fill={STICKER_INK} />
      <rect x={26} y={42} width={13} height={36} rx={3} fill={CORAL} />
      <rect x={81} y={42} width={13} height={36} rx={3} fill={CORAL} />
    </>
  ),

  // ── New moments ───────────────────────────────────────────────────────────
  "early-bee-3": (
    <>
      <path
        d="M60 32 V20 M30 46 l-8 -8 M90 46 l8 -8 M18 68 H8 M102 68 H112"
        fill="none"
      />
      <path d="M18 92 A42 42 0 0 1 102 92 Z" fill="#ff9f6e" />
      <path d="M8 92 H112" fill="none" strokeWidth={4.5} />
      <Bee transform="translate(60 72) scale(1.3)" />
    </>
  ),
  "night-shift-3": (
    <>
      <path
        d="M53.9 22.05 A40 40 0 1 0 93.3 76.35 A34 34 0 0 1 53.9 22.05 Z"
        fill="#ffe08a"
      />
      <path
        d="M98 18 l2.5 5 5.5 0.8 -4 3.8 1 5.4 -5 -2.6 -5 2.6 1 -5.4 -4 -3.8 5.5 -0.8 z M106 48 l1.8 3.6 4 0.6 -2.9 2.8 0.7 3.9 -3.6 -1.9 -3.6 1.9 0.7 -3.9 -2.9 -2.8 4 -0.6 z"
        fill={LILAC}
        stroke="none"
      />
      <Label x={34} y={54} size={13} fill={STICKER_INK}>
        z z
      </Label>
      <Bee transform="translate(46 86) scale(0.9)" />
    </>
  ),
  "pack-1": (
    <>
      <rect
        x={26}
        y={62}
        width={72}
        height={34}
        rx={7}
        fill={MINT}
        transform="rotate(6 62 76)"
      />
      <rect
        x={22}
        y={46}
        width={72}
        height={34}
        rx={7}
        fill={HONEY}
        transform="rotate(-4 58 62)"
      />
      <rect x={24} y={32} width={72} height={34} rx={7} fill={CREAM} />
      <rect x={54} y={26} width={12} height={78} fill={PURPLE} />
      <ellipse
        cx={50}
        cy={24}
        rx={11}
        ry={6}
        fill={PURPLE}
        transform="rotate(-20 50 24)"
      />
      <ellipse
        cx={70}
        cy={24}
        rx={11}
        ry={6}
        fill={PURPLE}
        transform="rotate(20 70 24)"
      />
      <circle cx={60} cy={26} r={5} fill={PURPLE} />
    </>
  ),
  "invite-1": (
    <>
      <rect x={12} y={38} width={84} height={60} rx={9} fill={CREAM} />
      <path d="M12 46 L54 74 L96 46" fill="none" />
      <circle cx={92} cy={36} r={18} fill={CORAL} />
      <Label x={92} y={42} size={17} fill={WHITE}>
        +1
      </Label>
    </>
  ),
  "distance-100": (
    <>
      <circle cx={60} cy={54} r={44} fill={CREAM} />
      <circle cx={60} cy={54} r={35} fill="none" strokeWidth={2} />
      <path d="M60 14 V22 M60 86 V94 M20 54 H28 M92 54 H100" fill="none" />
      <polygon points="60,26 69,54 51,54" fill={CORAL} />
      <polygon points="60,82 51,54 69,54" fill={STICKER_INK} />
      <circle cx={60} cy={54} r={4} fill={WHITE} />
      <rect x={34} y={94} width={52} height={22} rx={11} fill={STICKER_INK} />
      <Label x={60} y={110} size={13} fill={WHITE}>
        100 km
      </Label>
    </>
  ),
  "hexes-10": (
    <>
      <polygon
        points="60,40 73.86,48 73.86,64 60,72 46.14,64 46.14,48"
        fill={MINT}
      />
      <polygon
        points="46.1,64 59.96,72 59.96,88 46.1,96 32.24,88 32.24,72"
        fill="#3fb98a"
      />
      <polygon
        points="73.9,64 87.76,72 87.76,88 73.9,96 60.04,88 60.04,72"
        fill={MINT}
      />
      <path d="M60 56 V14" fill="none" strokeWidth={4.5} />
      <polygon points="60,14 94,22 60,32" fill={PURPLE} />
    </>
  ),
  "streak-7": (
    <>
      <path
        d="M60 14 C66 24, 76 30, 74 42 C72 50, 66 54, 60 54 C53 54, 46 50, 46 42 C46 32, 56 26, 60 14 Z"
        fill={CORAL}
      />
      <rect x={8} y={66} width={104} height={36} rx={12} fill={CREAM} />
      {[21, 34, 47, 60, 73, 86, 99].map((cx) => (
        <circle
          key={cx}
          cx={cx}
          cy={84}
          r={6}
          fill={cx === 99 ? CORAL : MINT}
        />
      ))}
    </>
  ),
  "founding-1": (
    <>
      <circle cx={60} cy={60} r={46} fill={PURPLE} />
      <circle
        cx={60}
        cy={60}
        r={37}
        fill="none"
        stroke={WHITE}
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <Label x={60} y={40} size={12} fill={WHITE}>
        EST.
      </Label>
      <Bee transform="translate(60 68) scale(1.4)" />
    </>
  ),

  // ── Feed ──────────────────────────────────────────────────────────────────
  "likes-50": (
    <>
      <path
        d="M60 104 C24 82, 14 54, 30 38 C42 26, 56 32, 60 42 C64 32, 78 26, 90 38 C106 54, 96 82, 60 104 Z"
        fill={PINK}
      />
      <path
        d={SMALL_HEART}
        fill={CORAL}
        transform="translate(100 24) scale(1.3)"
      />
      <path
        d={SMALL_HEART}
        fill={LILAC}
        transform="translate(18 26) scale(1.1)"
      />
      <path
        d="M44 52 A12 12 0 0 1 54 46"
        fill="none"
        stroke={WHITE}
        strokeWidth={4.5}
      />
    </>
  ),
  "poll-1": (
    <>
      <rect x={14} y={28} width={88} height={78} rx={14} fill={CREAM} />
      <rect x={26} y={42} width={58} height={13} rx={6.5} fill={PURPLE} />
      <rect x={26} y={62} width={38} height={13} rx={6.5} fill={LILAC} />
      <rect x={26} y={82} width={22} height={13} rx={6.5} fill={LILAC} />
      <path
        d="M98 6 C104 14, 112 18, 110 28 C109 34, 104 37, 98 37 C92 37, 87 33, 87 27 C87 19, 95 16, 98 6 Z"
        fill={CORAL}
      />
    </>
  ),

  // ── Activity sessions ─────────────────────────────────────────────────────
  "score-1": (
    <>
      <rect x={12} y={22} width={96} height={64} rx={13} fill={STICKER_INK} />
      <rect x={22} y={34} width={32} height={40} rx={7} fill={CREAM} />
      <rect x={66} y={34} width={32} height={40} rx={7} fill={CREAM} />
      <Label x={38} y={66} size={28} fill={STICKER_INK}>
        6
      </Label>
      <Label x={82} y={66} size={28} fill={STICKER_INK}>
        4
      </Label>
      <circle cx={60} cy={48} r={2.6} fill={WHITE} stroke="none" />
      <circle cx={60} cy={60} r={2.6} fill={WHITE} stroke="none" />
      <g transform="rotate(-38 84 100)">
        <rect x={62} y={95} width={36} height={11} rx={2} fill={HONEY} />
        <polygon points="98,95 110,100.5 98,106" fill={CREAM} />
        <rect x={56} y={95} width={8} height={11} rx={2} fill={PINK} />
      </g>
    </>
  ),
  "score-10": (
    <>
      <rect x={22} y={18} width={76} height={92} rx={10} fill={HONEY_DARK} />
      <rect x={30} y={30} width={60} height={72} rx={4} fill={WHITE} />
      <rect x={44} y={10} width={32} height={16} rx={5} fill={STICKER_INK} />
      {[44, 60, 76, 92].map((cy) => (
        <g key={cy}>
          <path
            d={`M37 ${cy} l4 4 7 -8`}
            fill="none"
            stroke={MINT}
            strokeWidth={4}
          />
          <path d={`M56 ${cy} H82`} fill="none" strokeWidth={2} />
        </g>
      ))}
    </>
  ),
  "rating-gain-25": (
    <>
      <polygon
        points="60,10 102,54 78,54 78,108 42,108 42,54 18,54"
        fill={MINT}
      />
      <Label x={60} y={88} size={13} fill={STICKER_INK}>
        +25
      </Label>
      <path
        d="M100 16 v10 M95 21 h10 M16 22 v8 M12 26 h8"
        fill="none"
        strokeWidth={2}
      />
    </>
  ),
  "sports-3": (
    <>
      <circle cx={60} cy={36} r={20} fill={WHITE} />
      <polygon
        points="60,28 67.6,33.5 64.7,42.5 55.3,42.5 52.4,33.5"
        fill={STICKER_INK}
        stroke="none"
      />
      <circle cx={38} cy={76} r={22} fill={LIME} />
      <path
        d="M20 64 Q38 76 20 88 M56 64 Q38 76 56 88"
        fill="none"
        stroke={WHITE}
        strokeWidth={3}
      />
      <circle cx={84} cy={76} r={22} fill={CORAL} />
      <path
        d="M62 76 H106 M84 54 V98 M69 60 Q80 76 69 92 M99 60 Q88 76 99 92"
        fill="none"
        strokeWidth={2}
      />
    </>
  ),
  "full-house-1": (
    <>
      <path d="M18 56 L60 18 L102 56 V96 H18 Z" fill={CORAL} />
      {[
        [32, 54],
        [70, 54],
        [32, 72],
        [70, 72],
      ].map(([x, y]) => (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width={18}
          height={12}
          rx={3}
          fill={HONEY}
        />
      ))}
      <rect x={28} y={92} width={64} height={22} rx={11} fill={STICKER_INK} />
      <Label x={60} y={108} size={14} fill={WHITE}>
        FULL
      </Label>
    </>
  ),
  "session-invites-5": (
    <>
      <path
        d="M8 98 C22 96, 30 86, 40 76"
        fill="none"
        strokeWidth={2}
        strokeDasharray={DASH}
      />
      <polygon points="14,58 108,18 74,102 58,70" fill={SKY} />
      <polygon points="58,70 108,18 66,94" fill="#4ea6ea" />
      <path d="M58 70 L108 18" fill="none" strokeWidth={2} />
    </>
  ),
  "split-settled-1": (
    <>
      <path d="M54 24 A36 36 0 0 0 54 96 Z" fill={HONEY} />
      <path d="M66 24 A36 36 0 0 1 66 96 Z" fill={HONEY} />
      <Label x={40} y={68} size={20} fill={STICKER_INK}>
        ₹
      </Label>
      <Label x={80} y={68} size={20} fill={STICKER_INK}>
        ₹
      </Label>
      <circle cx={92} cy={94} r={15} fill={MINT} />
      <path d="M85 94 l5 5 9 -10" fill="none" strokeWidth={3} />
    </>
  ),

  // ── Hiive Money ───────────────────────────────────────────────────────────
  "topup-1": (
    <>
      <path d="M60 4 V12 M48 8 l3 6 M72 8 l-3 6" fill="none" strokeWidth={2} />
      <circle cx={60} cy={24} r={10} fill="#ffe08a" />
      <Label x={60} y={29} size={13} fill={STICKER_INK}>
        +
      </Label>
      <rect x={28} y={46} width={64} height={62} rx={16} fill={HONEY} />
      <rect x={34} y={36} width={52} height={14} rx={5} fill={HONEY_DARK} />
      <rect x={40} y={64} width={40} height={26} rx={5} fill={CREAM} />
      <Label x={60} y={84} size={18} fill={STICKER_INK}>
        ₹
      </Label>
    </>
  ),
  "hiive-money-3": (
    <>
      <path d="M28 52 Q22 102 60 106 Q98 102 92 52 Z" fill={HONEY_DARK} />
      <ellipse cx={60} cy={52} rx={33} ry={9} fill={HONEY} />
      <path d="M40 54 V68 A5 5 0 0 0 50 68 V56" fill={HONEY} />
      <Label x={62} y={92} size={22} fill={CREAM}>
        ₹
      </Label>
      <Bee transform="translate(94 28) scale(0.9)" />
    </>
  ),
};
